import React, { FC, useEffect, useState } from 'react';
import { toAbsoluteUrl } from '../../utils';
import { BASE_URL } from '../../../config/environmentConfig';
import { useFormik } from 'formik';
import {
    getStripeSignUpDetailSchema,
    initialStripeSignUpValue,
    SIGN_UP_FORM_FIELD,
} from '../../constants/sign-up/sign-up-form';
import { useTranslation } from 'react-i18next';
import { SIGN_UP_INPUTS, STRIPE_SIGN_UP_INPUTS } from '../../constants/sign-up/sign-up-inputs';
import { FormInput } from '../../components/merchants/FormInput';
import { BUTTON_TYPE, ButtonComponent } from '../../components/ButtonComponent';
import { setSignUp } from '../../store/merchnts/actions/signUp';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { SITE_KEY } from '../login/Login';
import { ActionName, ILink } from '../../interfaces/links';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ConfirmPasswordInput } from '../../components/comfirmPassword/ConfirmPasswordInput';
import { LoginLayout } from '../../components/login/LoginLayout';
import { RedirectToLogin } from '../../components/login/RedirectToLogin';

import './StripeSignUp.scss';

export const StripeSignUp: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { merchantState: { signUpLoading }, dispatch } = useMerchants();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const formik = useFormik({
        initialValues: initialStripeSignUpValue,
        validationSchema: getStripeSignUpDetailSchema(t),
        onSubmit: () => setSignUp(dispatch, formik, handleSuccess, null, true),
    });

    useEffect(() => {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        const searchParams = location?.search.replace(/\+/g, '%2B'); // Replace + with %2B
        const params = new URLSearchParams(searchParams);
        const email = params.get('email');
        const companyName = params.get('companyName');
        const companyUrl = params.get('companyUrl');
        const companyPhone = params.get('companyPhone');
        const country = params.get('country');

        if (country?.toLowerCase() !== 'us') {
            return navigate('/stripe-country-decline');
        }

        if (email) {
            formik.setFieldValue(SIGN_UP_FORM_FIELD.EMAIL, email);
        }

        if (companyName) {
            formik.setFieldValue(SIGN_UP_FORM_FIELD.COMPANY_NAME, companyName);
        }

        if (companyUrl) {
            formik.setFieldValue(SIGN_UP_FORM_FIELD.WEBSITE, companyUrl);
        }

        if (companyPhone) {
            const phoneNumber = parsePhoneNumberFromString(companyPhone, 'US'); // Replace 'US' with the appropriate country code if needed
            if (phoneNumber) {
                formik.setFieldValue(SIGN_UP_FORM_FIELD.PHONE, phoneNumber.format('E.164'));
            }
        }
    }, [location?.search]);

    const hideRecaptcha = (): void => {
        const script = document.querySelector('.grecaptcha-badge');
        if (script) {
            // @ts-ignore
            script.style.visibility = 'hidden';
        }
    };

    const handleSuccess = (links?: ILink[]): void => {
        hideRecaptcha();

        const link = links?.find(link => link.actionName === ActionName.RedirectToStripeConnect);

        if (link && link.href && link.rel === 'external') {
            window.location.href = link.href;
        } else if (link && link.href) {
            navigate('/');
        }
    };

    return (
        <LoginLayout>

            <div className="form-container">
                <a
                    href={BASE_URL.LOGIN_URL}
                    target="_blank"
                    className="login-logo-link d-block text-center mb-10 w-100"
                    rel="noreferrer"
                >
                    <img
                      className="cursor-pointer login-logo"
                      src={toAbsoluteUrl('/media/flex-logo-small.svg')}
                      alt="logo"
                    />
                </a>

                <form
                    className="form w-100"
                    onSubmit={formik.handleSubmit}
                >
                    <h1 className="text-dark text-center">Get started</h1>
                    <p className="text-muted fs-6 mb-10 text-center">Create your {BASE_URL.APP_ENVIRONMENT} account</p>

                    <div className="row">
                        {
                            SIGN_UP_INPUTS().map(item => {
                                const { key, ...rest } = item;
                                return (
                                    <FormInput
                                        {...item}
                                        formik={formik}
                                        key={key}
                                    />
                                );
                            })
                        }

                        {
                            STRIPE_SIGN_UP_INPUTS({ showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword }).map(input => {
                                const { key, ...rest } = input;
                                return (
                                    <ConfirmPasswordInput
                                        key={key}
                                        formik={formik}
                                        required
                                        isSmallInput
                                        {...rest}
                                    />
                                );
                            })
                        }
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="stripe-signin-img-wrap w-100">
                                <img className="stripe-signin-img"
                                     src={toAbsoluteUrl('/media/stripe_sign_in.svg')} />
                            </div>

                            <div className="form-check fw-bold mb-3">
                                <input
                                    type="checkbox"
                                    name="allowAccess"
                                    checked={formik.values[SIGN_UP_FORM_FIELD.STRIPE]}
                                    onChange={(): void => formik.setFieldValue(SIGN_UP_FORM_FIELD.STRIPE, !formik.values[SIGN_UP_FORM_FIELD.STRIPE])}
                                    className="form-check-input"
                                />
                                <label className="form-check-label">
                                    <div className="fs-12">
                                        Allow FlexFactor to access my Stripe account to rescue transactions <span className="text-danger">*</span>
                                    </div>
                                </label>
                                {formik.touched[SIGN_UP_FORM_FIELD.STRIPE] && formik.errors[SIGN_UP_FORM_FIELD.STRIPE] && (
                                    <div className="error-container">
                                        <div
                                            className="text-danger fs-8">{formik.errors[SIGN_UP_FORM_FIELD.STRIPE]}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <ButtonComponent
                        buttonType={BUTTON_TYPE.DEFAULT_BLACK}
                        type="submit"
                        buttonName="Create account"
                        additionalClass="w-100 mt-10"
                        isDisabled={signUpLoading}
                        loading={signUpLoading}
                    />

                    <RedirectToLogin
                      additionalClass="mt-5 text-center fs-7"
                      hasAccount
                    />
                </form>
            </div>
        </LoginLayout>
    );
};
